import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { BRAND, SERVICES, IMAGES } from "@/lib/constants";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO — editorial, asymmetric */}
      <section className="relative overflow-hidden">
        <div className="aura-bg" />
        <div className="noise-overlay" />
        <div className="relative z-10 page-shell pt-8 md:pt-12 pb-20 md:pb-32">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {/* Vertical label */}
            <div className="hidden md:flex col-span-1 items-end pb-12">
              <div className="vertical-text text-lavender-700">
                Est. 2018 — Spiritüel Rehberlik
              </div>
            </div>

            {/* Headline cluster */}
            <div className="col-span-12 md:col-span-8 pt-10 md:pt-16 min-w-0">
              <div className="eyebrow mb-8">{BRAND.name}</div>
              <h1 className="font-display text-[17vw] sm:text-[14vw] md:text-[9.5vw] lg:text-[8.5rem] leading-[0.95] font-light text-midnight tracking-normal fade-up">
                Ruhun
                <span className="block italic font-light text-lavender-700">
                  <em className="not-italic font-normal">sessiz</em>
                  <br className="sm:hidden" /> rehberi.
                </span>
              </h1>
              <div className="h-px bg-gold w-32 mt-10 draw-line" />
            </div>

            {/* Side block */}
            <div className="col-span-12 md:col-span-3 md:col-start-10 flex flex-col sm:flex-row md:flex-col justify-between md:justify-end gap-6 md:gap-8 mt-8 md:mt-0">
              <p className="font-body text-[15px] leading-relaxed text-lavender-700 max-w-none md:max-w-xs">
                Frekanslarla şifa, soy karması okumaları, numeroloji ve sezgisel
                rehberlik. Modern yaşamın gürültüsünde kendi merkezine dönmen
                için zarif bir alan.
              </p>
              <Link
                to="/randevu"
                data-testid="hero-cta-appointment"
                className="btn-midnight self-start md:self-stretch md:justify-center"
              >
                Randevu Al <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          {/* Bento images strip */}
          <div className="grid grid-cols-12 gap-4 md:gap-6 mt-14 md:mt-20">
            <div className="col-span-12 md:col-span-7 img-zoom rounded-sm aspect-[16/9]">
              <img src={IMAGES.water} alt="suyun dingin yansıması" />
            </div>
            <div className="col-span-7 md:col-span-3 img-zoom rounded-sm aspect-[3/4]">
              <img src={IMAGES.crystal} alt="kristal" />
            </div>
            <div className="col-span-5 md:col-span-2 flex flex-col justify-end pb-4">
              <Sparkles className="text-gold mb-3" size={20} />
              <div className="font-display italic text-2xl leading-tight text-midnight">
                "Her ruhun bir frekansı, her frekansın bir hatırlatması vardır."
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-lavender-700 mt-3">
                — {BRAND.shortName}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH — alternating editorial */}
      <section className="relative overflow-hidden bg-cream-100/40 py-20 md:py-32" data-testid="home-approach">
        <div className="page-shell grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4 min-w-0">
            <div className="eyebrow mb-6">Yaklaşımım</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-light text-midnight leading-[1.05] tracking-normal">
              Yargısız bir
              <br />
              <em className="text-lavender-700">alan</em> · sezgisel bir
              <em className="text-gold-soft"> dil</em>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 min-w-0 max-w-full space-y-10">
            {[
              {
                n: "01",
                t: "Bedensel & Enerjisel Hizalanma",
                d: "Çalışmalarımızda yalnızca zihinle değil, beden hafızası ve enerji alanıyla birlikte ilerleriz. Bütüncül bir hizalanma temel niyetimizdir.",
              },
              {
                n: "02",
                t: "Sezgisel Okuma · Bilimsel Çerçeve",
                d: "Numeroloji ve sembolik çalışmalar; modern psikoloji ve mindfulness çerçevesiyle desteklenir. Mistik söylem değil, anlamlı yapı.",
              },
              {
                n: "03",
                t: "Gizlilik & Güven",
                d: "Tüm seanslar mahrem ve yargısızdır. Paylaştıkların yalnızca seninle kalır; alanı kutsamak temel ilkemizdir.",
              },
            ].map((it) => (
              <div
                key={it.n}
                className="grid w-full max-w-full grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-6 pb-8 md:pb-10 border-b border-cream-300"
              >
                <div className="number-marker text-xl sm:col-span-2 sm:text-2xl">
                  {it.n}
                </div>
                <div className="min-w-0 max-w-full sm:col-span-10">
                  <h3 className="font-display text-2xl md:text-3xl text-midnight mb-3 font-normal">
                    {it.t}
                  </h3>
                  <p className="max-w-full [overflow-wrap:anywhere] text-lavender-700 leading-relaxed">
                    {it.d}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW — tetris grid */}
      <section className="py-20 md:py-32" data-testid="home-services-preview">
        <div className="page-shell">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <div className="eyebrow mb-6">Hizmetler</div>
              <h2 className="font-display text-4xl sm:text-5xl md:text-7xl font-light text-midnight leading-[1] tracking-normal max-w-3xl">
                Sana özel altı
                <br />
                <em className="text-lavender-700">çalışma alanı</em>.
              </h2>
            </div>
            <Link
              to="/hizmetler"
              data-testid="home-services-all-link"
              className="link-gold text-sm tracking-[0.22em] uppercase"
            >
              Tümünü Gör <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-12 gap-px bg-cream-300 border border-cream-300">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/hizmetler#${s.slug}`}
                data-testid={`home-service-${s.slug}`}
                className={`group bg-cream-50 hover:bg-cream-100 transition-colors duration-700 p-6 md:p-10 ${
                  i % 3 === 0
                    ? "col-span-12 md:col-span-5"
                    : i % 3 === 1
                    ? "col-span-12 md:col-span-4"
                    : "col-span-12 md:col-span-3"
                }`}
              >
                <div className="flex items-start justify-between">
                  <span className="number-marker">0{i + 1}</span>
                  <ArrowUpRight
                    size={18}
                    className="text-lavender-700 group-hover:text-gold transition-colors duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </div>
                <h3 className="font-display text-3xl md:text-4xl text-midnight mt-8 md:mt-12 leading-tight">
                  {s.title}
                </h3>
                <p className="text-sm text-lavender-700 mt-4 leading-relaxed">
                  {s.short}
                </p>
                <div className="mt-8 pt-5 border-t border-cream-300 text-[10px] tracking-[0.3em] uppercase text-gold-deep flex justify-between">
                  <span>{s.duration}</span>
                  <span>{s.format.split("·")[0]}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASE */}
      <section className="py-20 md:py-32 bg-lavender-50/40 relative" data-testid="home-about-tease">
        <div className="page-shell grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 md:col-span-5 img-zoom rounded-sm aspect-[3/4]">
            <img src={IMAGES.about} alt="Yeliz Kıran Pastırmacıoğlu" />
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <div className="eyebrow mb-6">Tanışalım</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-light text-midnight leading-[1.05] tracking-normal">
              On yılı aşkın
              <br />
              sezgisel
              <em className="text-gold-soft"> yolculuk</em>.
            </h2>
            <p className="text-lavender-700 mt-8 leading-relaxed max-w-xl text-[15px]">
              Eğitimini Türkiye ve Avrupa'da spiritüel yaşam koçluğu, frekans
              çalışmaları ve aile dizimi alanlarında tamamladım. Bugüne dek 2.000'i
              aşkın kişiye eşlik etme onuruna sahip oldum.
            </p>
            <div className="mt-10 md:mt-12 grid grid-cols-2 sm:flex sm:flex-wrap items-start gap-6 sm:gap-10">
              {[
                ["2000+", "Birebir seans"],
                ["12", "Yıllık deneyim"],
                ["6", "Uzmanlık alanı"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-5xl text-midnight font-light">
                    {n}
                  </div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-lavender-700 mt-1">
                    {l}
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/hakkinda"
              data-testid="home-about-link"
              className="link-gold mt-12 text-sm tracking-[0.22em] uppercase"
            >
              Hikayemi Oku &nbsp;→
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — marquee */}
      <section className="py-20 md:py-32 overflow-hidden" data-testid="home-testimonials">
        <div className="page-shell mb-12 md:mb-16">
          <div className="eyebrow mb-6">Danışanlarımdan</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-light text-midnight leading-[1.05] max-w-3xl">
            "Bir alanın ışığında <em className="text-lavender-700">kendine</em> dönüş."
          </h2>
        </div>
        <div className="relative">
          <div className="flex gap-6 marquee-track" style={{ width: "max-content" }}>
            {[...Array(2)].map((_, dup) =>
              [
                {
                  q: "Numeroloji seansından sonra hayatımdaki tekrarlayan kalıpları ilk kez gerçekten gördüm. Hayatımın akışı değişti.",
                  a: "Selin K.",
                },
                {
                  q: "Soy karması çalışması inanılmaz derindi. Anneannemden taşıdığım yüklerin farkına vardım — bedenim hafifledi.",
                  a: "Cansu D.",
                },
                {
                  q: "Frekans seansı sonrası ilk kez uyku ilacı almadan uyudum. Yargısız alan eşsizdi.",
                  a: "Ebru A.",
                },
                {
                  q: "Akış terapisi bana yeniden nefes almayı öğretti. Profesyonelliği ve şefkati kelimelerle ifade edilemez.",
                  a: "Pınar Y.",
                },
              ].map((t, i) => (
                <div
                  key={`${dup}-${i}`}
                  className="w-[420px] shrink-0 p-10 border border-cream-300 bg-cream-50"
                >
                  <div className="text-gold text-3xl font-display leading-none">"</div>
                  <p className="font-display text-2xl text-midnight italic leading-snug mt-4">
                    {t.q}
                  </p>
                  <div className="mt-8 text-[11px] tracking-[0.3em] uppercase text-lavender-700">
                    — {t.a}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-20 md:py-32 bg-midnight text-cream-50 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gold/20 blur-[100px]" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-lavender-700/30 blur-[100px]" />
        <div className="relative page-shell page-shell-narrow text-center">
          <Sparkles className="mx-auto text-gold-soft mb-8" />
          <h2 className="font-display text-5xl sm:text-6xl md:text-8xl font-light leading-[1] tracking-normal">
            İlk adım
            <br />
            <em className="text-gold-soft">senin</em> sezgindir.
          </h2>
          <p className="mt-10 text-cream-100/70 max-w-xl mx-auto leading-relaxed">
            Hazır hissettiğin an, bir mesaj uzaktayız. WhatsApp üzerinden seans
            türüne göre ön görüşme planlayabiliriz.
          </p>
          <Link
            to="/randevu"
            data-testid="home-closing-cta"
            className="inline-flex w-full sm:w-auto items-center justify-center gap-3 mt-10 md:mt-12 px-6 sm:px-10 py-4 bg-cream-50 text-midnight rounded-full text-xs sm:text-sm tracking-[0.14em] md:tracking-[0.22em] uppercase hover:bg-gold hover:text-cream-50 transition-all duration-500"
          >
            Randevuyu Başlat <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
